import React from "react";
import type {AddCatchWidgetApiInitFaults, RenderFunction} from "@skbkontur/loader-builder";
import type {RenderDisposeFault as RenderDisposeFaultInternal, RenderFault as RenderFaultInternal} from "./private.js";
import type {RenderParamsFias, RenderParamsFiasCombobox} from "./index.js";
import type {OperationResult} from "@skbkontur/operation-result";
import {FiasData} from "./FiasData.js";

export enum FiasRoomType {
  None = 0,
  Room = 1,
  Placement = 2,
}

export enum FiasFormValidation {
  Error = 'Error',
  Warning = 'Warning',
  None = 'None',
}
export enum FiasFields {
  region = 'region',
  district = 'district',
  municipaldistrict = 'municipaldistrict',
  city = 'city',
  intracityarea = 'intracityarea',
  urbansettlement = 'urbansettlement',
  settlement = 'settlement',
  planningstructure = 'planningstructure',
  street = 'street',
  stead = 'stead',
  house = 'house',
  garhouse = 'garhouse',
  room = 'room',
  apartment = 'apartment',
}

export const RUSSIA = {
  shortName: 'Россия',
  fullName: 'Российская Федерация',
  code: '643',
};

export const MAIN_FIELDS: FiasFields[] = [
  FiasFields.region,
  FiasFields.municipaldistrict,
  FiasFields.district,
  FiasFields.urbansettlement,
  FiasFields.city,
  FiasFields.intracityarea,
  FiasFields.settlement,
  FiasFields.planningstructure,
  FiasFields.street,
  FiasFields.stead,
  FiasFields.garhouse,
  FiasFields.house,
  FiasFields.room,
];

export enum FiasExtraFields {
  postalcode = 'postalcode',
}

export const ADDITIONAL_FIELDS: FiasExtraFields[] = [FiasExtraFields.postalcode];

export const ALL_FIELDS: (FiasFields | FiasExtraFields)[] = [...MAIN_FIELDS, ...ADDITIONAL_FIELDS];

export interface FiasFullValue {
  address: FiasAddressValue;
  addressString: string;
  addressErrors: FiasAddressErrors;
  fiasId: string;
  postalCode: string;
  country?: FiasCountry | undefined;
  foreignAddress?: string;
  addressWithOriginalFields?: FiasAddressValue;
  division?: FiasDivision | undefined;
}
export type FiasAllFields = FiasFields | FiasExtraFields;
export enum FiasFieldErrorType {
  unknown = "unknown",
  notAllowedToFill = "notAllowedToFill",
  notValid = "notValid",
}
export interface FiasFieldError {
  label: React.ReactNode;
  type?: string | undefined;
}
export type FiasAddressErrors = {
  [key in FiasAllFields]?: FiasFieldError;
};
export enum FiasFlatType {
  None = 0,
  Placement = 1,
  Apartment = 2,
  Office = 3,
  Room = 4,
  WorkArea = 5,
  Stock = 6,
  ShoppingRoom = 7,
  Workshop = 8,
  Pavilion = 9,
  Basement = 10,
  BoilerRoom = 11,
  Cellar = 12,
  Garage = 13,
  CarPlace = 14,
}
export interface FiasRoom extends FiasEntity {
  flatNumber: string;
  flatType: FiasFlatType;
  liveStatus: FiasLiveStatuses;
  flatFiasId?: FiasId;
  roomNumber?: string;
  roomType?: FiasRoomType;
  flatAbbreviation: string;
}
export type FiasObject = FiasAddressObject | FiasStead | FiasHouse | GarHouse | FiasRoom;
export type FiasId = string;
export interface FiasEntity {
  id: string;
  fiasId: FiasId;
  level?: string;
  parentFiasId?: FiasId;
  parentFiasIds?: FiasId[];
  postalCode?: string;
  okato?: string;
  oktmo?: string;
  ifnsfl?: string;
  ifnsul?: string;
}
export interface FiasAddressObject extends FiasEntity {
  name: string;
  official: string;
  abbreviation: string;
  code?: string;
}
export interface FiasStead extends FiasEntity {
  number: string;
  liveStatus: FiasLiveStatuses;
}
export enum FiasLiveStatuses {
  active = 'active',
  inactive = 'inactive',
}
export interface FiasHouse extends FiasEntity {
  estateStatus: FiasEstateStatuses;
  structureStatus: FiasStructureStatuses;
  number?: string;
  structureNumber?: string;
  buildingNumber?: string;
}
export enum FiasEstateStatuses {
  None = 'None',
  Hold = 'Hold',
  House = 'House',
  HouseHold = 'HouseHold',
  Garage = 'Garage',
  Building = 'Building',
  Mine = 'Mine',
  Basement = 'Basement',
  BoilerRoom = 'BoilerRoom',
  Cellar = 'Cellar',
}
export enum FiasStructureStatuses {
  Structure = 'Structure',
  Construction = 'Construction',
  Liter = 'Liter',
  None = 'None',
}
export interface GarHouse extends FiasEntity {
  houseNum?: string;
  houseType?: GarHouseType;
  addNum1?: string;
  addType1?: GarHouseAddType;
  addNum2?: string;
  addType2?: GarHouseAddType;
  houseAbbreviation?: string;
}
export enum FiasSuggestLevels {
  House = "House",
  Room = "Room"
}
export enum GarHouseType {
  None = 'None',
  Hold = 'Hold',
  House = 'House',
  HouseHold = 'HouseHold',
  Garage = 'Garage',
  Building = 'Building',
  Mine = 'Mine',
  Structure = 'Structure',
  Construction = 'Construction',
  Litera = 'Litera',
  Housing = 'Housing',
  Basement = 'Basement',
  BoilerRoom = 'BoilerRoom',
  Cellar = 'Cellar',
  UnfinishedConstruction = 'UnfinishedConstruction',
}
export enum GarHouseAddType {
  None = 'None',
  Structure = 'Structure',
  Construction = 'Construction',
  Litera = 'Litera',
  Housing = 'Housing',
}
export enum FiasDivision {
  Administrative = 'administrative',
  Municipal = 'municipal',
}
export enum FiasSchema {
  Fias = 'fias',
  Gar = 'gar',
}
export interface FiasCountry {
  shortName?: string;
  fullName?: string;
  code: string;
}
export interface IFiasAddressElement {
  isFederalCity: boolean;
  isMoscowRegion: boolean;
  isUnknown: boolean;
  fiasData: FiasObject | undefined;
  fiasId: FiasId | undefined;
  editableString: string;
  getText(withoutType: boolean): string;
  isTypeMatchField(field: FiasFields): boolean;
  // verifiableData todo
  removeData(): void;
  isEqualTo(element: IFiasAddressElement | undefined): boolean;
}
export type FiasFieldValue<Field extends FiasAllFields> = Field extends FiasFields
  ? IFiasAddressElement
  : Field extends FiasExtraFields.postalcode
    ? string
    : never;
export interface FiasValidationFunc<Field extends FiasAllFields> {
  (value: FiasFieldValue<Field> | undefined, field: Field, fiasError: FiasFieldError | null): FiasFieldError;
}
export interface FiasFieldSettings<Field extends FiasAllFields> {
  visible?: boolean;
  validation?: boolean | FiasValidationFunc<Field>;
}
export type FiasFieldsSettings = {
  [Field in FiasAllFields]?: FiasFieldSettings<Field>;
};
export type FiasAddressValue = {
  [key in FiasFields]?: {
    name: string;
    text: string;
    data?: FiasObject;
  };
}

export const isForeign = (value?: Partial<FiasValue>): boolean => {
  return Boolean(value?.country && !IsRussia(value?.country));
}

export const IsRussia = (country: FiasCountry): boolean => {
  return country.code === RUSSIA.code;
};

export const hasMainFields = (value?: FiasAddressValue): boolean => {
  return MAIN_FIELDS.some((field) => Object.prototype.hasOwnProperty.call(value || {}, field));
}

export const getParentFields = (field: FiasFields): FiasFields[] => {
  const index = MAIN_FIELDS.indexOf(field);
  return index > -1 ? MAIN_FIELDS.slice(0, index) : [];
};

export const getText = (value?: Partial<FiasValue>, minField?: FiasFields, connector = ', '): string => {
  if (!hasMainFields(value?.address)) {
    return '';
  }
  const fields = minField ? getParentFields(minField) : MAIN_FIELDS;
  return fields
    .map((field) => value?.address?.[field]?.text)
    .filter(Boolean)
    .join(connector);
};

export const getPostalCode = (v?: Partial<FiasValue>): string => {
  const value = v?.postalCode;
  return typeof value === 'string' ? value : getFiasPostalCode(v?.address);
}

export const getFiasPostalCode = (value?: FiasAddressValue): string => {
  if (hasMainFields(value)) {
    const fields = MAIN_FIELDS.slice().reverse();
    for (const field of fields) {
      const elem = value?.[field];
      if (elem && elem.data && elem.data.postalCode) {
        return elem.data.postalCode;
      }
    }
  }
  return '';
};

export type GetFormState = () => Promise<{ value: FiasFullValue; addressHasErrors: boolean }>;
export type FiasValue = Partial<Omit<FiasFullValue, "fiasId" | "addressString" | "division">>;
export interface FiasFormProps {
  fieldsSettings?: FiasFieldsSettings | undefined;
  // search?: boolean | undefined;
  limit?: number | undefined;
  validationLevel?: FiasFormValidation | undefined;
  countrySelector?: boolean | undefined;
  divisionSelector?: boolean | undefined;
  schema: FiasSchema;
  division: FiasDivision;
  value?: FiasValue | undefined;
  suggestUnknownLevel?: FiasSuggestLevels | undefined;
  version?: string | undefined;
}

export type FiasComboBoxProps = {
  version?: string;
  countrySelector?: boolean;
  fieldsSettings?: FiasFieldsSettings | undefined;
  division: FiasDivision;
  schema: FiasSchema;
  suggestUnknownLevel?: FiasSuggestLevels | undefined;
  onValueChange?: (value: FiasFullValue) => void;
  divisionSelector?: boolean;
  onClose?: () => void;
  validationLevel?: FiasFormValidation | undefined;
  initialValue?: FiasValue | undefined;
  limit?: number | undefined;
  initialFormValue?: { countryCode?: string } | undefined;
  viewMode?: "singleline" | "multiline" | "multiline-editing" | undefined;
  maxRows?: number | undefined;
  align?: "left" | "center" | "right";
  disabled?: boolean;
  size?: "small" | "medium" | "large";
  width?: string | number | undefined;
  menuAlign?: "left" | "right";
  modalMode?: "modal" | "sidepage";
  modalWidth?: string | number;
};

export const isAddressEmpty = (value?: Partial<FiasValue>): { value: boolean } => {
  return { value: isForeign(value) ? !value?.foreignAddress : !hasMainFields(value?.address) };
}
type FiasPartValue = Partial<Pick<FiasValue, "address" | "postalCode" | "country" | "foreignAddress">>;
export const getAddressText = (params: { value?: FiasPartValue | undefined; countrySelector?: boolean | undefined; fieldsSettings?: FiasFieldsSettings | undefined; division: FiasDivision; schema: FiasSchema }): { value: string } => {
  const mergedFieldsSettings = getFieldsSettings({
    userFieldsSettings: params.fieldsSettings,
    countrySelector: params.countrySelector,
    division: params.division,
    schema: params.schema,
  });

  const substrings: string[] = [];

  if (mergedFieldsSettings.postalcode?.visible) {
    substrings.push(getPostalCode(params.value));
  }

  if (params.value?.country && params.value.country.fullName) {
    substrings.push(params.value.country.fullName);
  }

  substrings.push(isForeign(params.value) ? (params.value?.foreignAddress || "") : getText(params.value));

  return { value: substrings.filter(Boolean).join(', ') };
};

export const getDefaultFieldVisibility = (
  field: FiasAllFields,
  division: FiasDivision,
  schema: FiasSchema
): boolean => {
  switch (field) {
    case FiasFields.district:
      return division === FiasDivision.Administrative;
    case FiasFields.municipaldistrict:
    case FiasFields.urbansettlement:
      return division === FiasDivision.Municipal;
    case FiasFields.garhouse:
      return schema === FiasSchema.Gar;
    case FiasFields.house:
      return schema === FiasSchema.Fias;
    default:
      return true;
  }
}

export type FiasWidgetApi = {
  readonly renderFiasForm: (params: RenderParamsFias) => Promise<OperationResult<AddCatchWidgetApiInitFaults<RenderFaultInternal>, { dispose: () => Promise<OperationResult<RenderDisposeFaultInternal, void>>; getState: GetFormState }>>;
  readonly renderFiasCombobox: RenderFunction<RenderParamsFiasCombobox, RenderFaultInternal, RenderDisposeFaultInternal>;
};

export type FiasAddressResponse = { [key in FiasFields]?: FiasObject } & { suggest?: { fiasId: string | null; matchResult: "MATCH" | "NOT_STRICT_MATCH"; value: string } };

function deepMerge<T>(dst: T, ...src: T[]): T {
  src.forEach((obj) => {
    for (const k in obj) {
      if (dst[k] != null && typeof obj[k] === 'object') {
        dst[k] = deepMerge(dst[k], obj[k]);
      } else {
        dst[k] = obj[k];
      }
    }
  });
  return dst;
}
export const getFieldsSettings = ({
  userFieldsSettings,
  division,
  schema,
  countrySelector,
}: {
  userFieldsSettings?: FiasFieldsSettings | undefined;
  division: FiasDivision;
  schema: FiasSchema;
  countrySelector?: boolean | undefined;
}): FiasFieldsSettings => {
  // TODO: implement deepMerge with clone
  return deepMerge<FiasFieldsSettings>(
    getDefaultFieldsSettings({ division, schema }),
    {
      [FiasExtraFields.postalcode]: {
        visible: Boolean(countrySelector),
      },
    },
    userFieldsSettings || {},
  );
};

export const getDefaultFieldsSettings = (
  { division, schema }: { division: FiasDivision; schema: FiasSchema } = { division: defaultDivision, schema: FiasSchema.Fias }
): FiasFieldsSettings =>
  ALL_FIELDS.reduce<FiasFieldsSettings>(
    (settings: FiasFieldsSettings, field: FiasAllFields) => ({
      ...settings,
      [field]: {
        visible: getDefaultFieldVisibility(field, division, schema),
        validation: true,
      },
    }),
    {} as FiasFieldsSettings
  );

export const defaultDivision: FiasDivision = FiasDivision.Administrative;

export const defaultSchema: FiasSchema = FiasSchema.Fias;

export const getFieldText = ({ field, data, withoutType, name }: { field: FiasFields; data: FiasData | undefined; withoutType?: boolean; name: string }): string => {
  let result = '';

  if (!name) {
    return '';
  }

  if (data) {
    const { abbreviation, official, number } = data;
    if (abbreviation) {
      const type = !withoutType ? abbreviations[abbreviation] || abbreviation : '';
      switch (abbreviation) {
        case 'п':
          result = !withoutType
            ? `${field === FiasFields.district ? 'поселение' : 'поселок'} ${name}`
            : `${name}`;
          break;

        default:
          result = `${type} ${name}`;
      }
    }

    if (FiasData.isRegion(data.data)) {
      result = official;
    }

    if (FiasData.isStead(data.data)) {
      result = 'земельный участок ' + number;
    }

    if (FiasData.isHouse(data.data)) {
      const { estateStatus, structureStatus, structureNumber, buildingNumber, number } = data.data;

      result = `${result} ${estateStatuses[estateStatus]}`;

      if (number) {
        result = `${result} ${number}`;
      }

      if (structureStatus && structureStatus !== FiasStructureStatuses.None && structureNumber) {
        switch (structureStatus) {
          case FiasStructureStatuses.Structure:
            result = `${result} строение ${structureNumber}`;
            break;
          case FiasStructureStatuses.Construction:
            result = `${result} cооружение ${structureNumber}`;
            break;
          case FiasStructureStatuses.Liter:
            result = `${result} литера ${structureNumber}`;
            break;
        }
      }

      if (buildingNumber) {
        result = `${result} корпус ${buildingNumber}`;
      }
    }

    if (FiasData.isGarHouse(data.data)) {
      const { houseNum, houseType, addNum1, addNum2, addType1, addType2 } = data;
      result = houseNum ?? '';

      if (houseType && houseNum) {
        result = `${houseTypes[houseType]} ${houseNum}`;
      }
      if (addType1 && addNum1) {
        result += ` ${houseAddTypes[addType1]} ${addNum1}`;
      }
      if (addType2 && addNum2) {
        result += ` ${houseAddTypes[addType2]} ${addNum2}`;
      }
    }

    if (FiasData.isRoom(data.data)) {
      const { flatType, flatFiasId, roomType, roomNumber } = data.data
      if (flatType) {
        result = `${flatTypes[flatType]} ${name}`;
        if (flatFiasId && roomType && roomNumber) {
          result += ` ${roomTypes[roomType]} ${roomNumber}`;
        }
      } else {
        result = name;
      }
    }
  }

  return result.trim() || name;
}

const abbreviations: { [key: string]: string } = {
  АО: 'автономный округ',
  Аобл: 'автономная область',
  Респ: 'Республика',
  Чувашия: 'Чувашская Республика',
  г: 'город',
  'г.ф.з.': 'город федерального значения',
  край: 'край',
  обл: 'область',
  округ: 'округ',

  'вн.тер.г.': 'внутригородская территория',
  'г.о.': 'городской округ',
  'м.р-н': 'муниципальный район',
  'р-н': 'район',
  тер: 'территория',
  у: 'улус',

  волость: 'волость',
  дп: 'дачный поселок',
  кп: 'курортный поселок',
  массив: 'массив',
  п: 'поселок',
  'п/о': 'почтовое отделение',
  пгт: 'поселок городского типа',
  рп: 'рабочий поселок',
  'с/а': 'сельская администрация',
  'с/мо': 'сельское муницип.образование',
  'с/о': 'сельский округ',
  'с/п': 'сельское поселение',
  'с/с': 'сельсовет',

  аал: 'аал',
  автодорога: 'автодорога',
  арбан: 'арбан',
  аул: 'аул',
  высел: 'выселки(ок)',
  городок: 'городок',
  гп: 'городской поселок',
  д: 'деревня',
  'ж/д_будка': 'железнодорожная будка',
  'ж/д_казарм': 'железнодорожная казарма',
  'ж/д_оп': 'ж/д останов. (обгонный) пункт',
  'ж/д_платф': 'железнодорожная платформа',
  'ж/д_пост': 'железнодорожный пост',
  'ж/д_рзд': 'железнодорожный разъезд',
  'ж/д_ст': 'железнодорожная станция',
  жилзона: 'жилая зона',
  жилрайон: 'жилой район',
  заимка: 'заимка',
  казарма: 'казарма',
  'кв-л': 'квартал',
  кордон: 'кордон',
  лпх: 'леспромхоз',
  м: 'местечко',
  мкр: 'микрорайон',
  нп: 'населенный пункт',
  остров: 'остров',
  'п/р': 'промышленный район',
  'п/ст': 'поселок и(при) станция(и)',
  погост: 'погост',
  починок: 'починок',
  промзона: 'промышленная зона', // Промзона?
  рзд: 'разъезд',
  с: 'село',
  сл: 'слобода',
  снт: 'садовое неком-е товарищество',
  ст: 'станция',
  'ст-ца': 'станица',
  х: 'хутор',

  'а/я': 'абонентский ящик',
  аллея: 'аллея',
  'б-р': 'бульвар',
  балка: 'балка',
  берег: 'берег',
  бугор: 'бугор',
  вал: 'вал',
  'взв.': 'взвоз',
  въезд: 'въезд',
  гск: 'гаражно-строительный кооп.',
  днп: 'дачное неком-е партнерство',
  дор: 'дорога',
  'ж/р': 'жилой район',
  жт: 'животноводческая точка',
  заезд: 'заезд',
  зона: 'зона',
  км: 'километр',
  кольцо: 'кольцо',
  коса: 'коса',
  линия: 'линия',
  маяк: 'маяк',
  'мгстр.': 'магистраль',
  местность: 'местность',
  'месторожд.': 'месторождение',
  мост: 'мост',
  'н/п': 'некоммерческое партнерство',
  наб: 'набережная',
  'п-к': 'починок',
  парк: 'парк',
  пер: 'переулок',
  переезд: 'переезд',
  пл: 'площадь',
  'пл-ка': 'площадка',
  платф: 'платформа',
  полустанок: 'полустанок',
  порт: 'порт',
  'пр-кт': 'проспект',
  проезд: 'проезд',
  просек: 'просек',
  просека: 'просека',
  проселок: 'проселок',
  проулок: 'проулок',
  ряд: 'ряд(ы)',
  ряды: 'ряды',
  'с/т': 'садовое товарищество',
  сад: 'сад',
  'сзд.': 'съезд',
  сквер: 'сквер',
  спуск: 'спуск',
  стр: 'строение',
  'тер.': 'территория',
  'тер. ГСК': 'территория ГСК',
  'тер. ДНО': 'территория ДНО',
  'тер. ДНП': 'территория ДНП',
  'тер. ДНТ': 'территория ДНТ',
  'тер. ДПК': 'территория ДПК',
  'тер. ОНО': 'территория ОНО',
  'тер. ОНП': 'территория ОНП',
  'тер. ОНТ': 'территория ОНТ',
  'тер. ОПК': 'территория ОПК',
  'тер. СНО': 'территория СНО',
  'тер. СНП': 'территория СНП',
  'тер. СНТ': 'территория СНТ',
  'тер. СПК': 'территория СПК',
  'тер. ТСН': 'территория ТСН',
  'тер.ф.х.': 'территория ФХ',
  тракт: 'тракт',
  туп: 'тупик',
  ул: 'улица',

  'ус.': 'усадьба',
  'уч-к': 'участок',
  'ф/х': 'фермерское хозяйство',
  ферма: 'ферма',
  ш: 'шоссе',
  'ю.': 'юрты',

  ДОМ: 'дом',
  ОНС: 'объект незав. строительства',
  'влд.': 'владение',
  'двлд.': 'домовладение',
  'зд.': 'здание',
  'к.': 'корпус',
  'кот.': 'котельная',
  'пав.': 'павильон',
  'соор.': 'сооружение',
  шахта: 'шахта',

  // types from https://api.kontur.ru/fias/v1/addresses/types

  'а.обл.': 'автономная область',
  'а.окр.': 'автономный округ',
  'г.': 'город',
  'обл.': 'область',
  'респ.': 'республика',
  'пос.': 'поселение',
  'у.': 'улус',
  'м.о.': 'муниципальный округ',
  'вн.р-н': 'внутригородской район',
  'г.п.': 'городское поселение',
  'межсел.тер.': 'межселенная территория',
  'с.п.': 'сельское поселение',
  'пгт.': 'поселок городского типа',
  'г-к': 'городок',
  'дп.': 'дачный поселок',
  'ж/д б-ка': 'железнодорожная будка',
  'ж/д в-ка': 'железнодорожная ветка',
  'ж/д к-ма': 'железнодорожная казарма',
  'ж/д пл-ма': 'железнодорожная платформа',
  'ж/д пл-ка': 'железнодорожная площадка',
  'ж/д бл-ст': 'железнодорожный блокпост',
  'ж/д к-т': 'железнодорожный комбинат',
  'ж/д о.п.': 'железнодорожный остановочный пункт',
  'ж/д п.п.': 'железнодорожный путевой пост',
  'зим.': 'зимовье',
  'киш.': 'кишлак',
  'нп.': 'населенный пункт',
  'пл.р-н': 'планировочный район',
  'п. ж/д ст.': 'поселок при железнодорожной станции',
  'п. ст.': 'поселок при станции (поселок станции)',
  'пос.рзд': 'поселок разъезда',
  сп: 'сельский поселок',
  'сп.': 'сельский поселок',
  'ал.': 'аллея',
  'б-г': 'берег',
  'взд.': 'въезд',
  'д.': 'деревня',
  'дор.': 'дорога',
  'ж/д ст.': 'железнодорожная станция',
  'ж/д рзд.': 'железнодорожный разъезд',
  'ззд.': 'заезд',
  'к-цо': 'кольцо',
  'лн.': 'линия',
  'м-ко': 'местечко',
  'мр.': 'месторождение',
  'мкр.': 'микрорайон',
  'наб.': 'набережная',
  'ост-в': 'остров',
  'пер-д': 'переезд',
  'пер.': 'переулок',
  'платф.': 'платформа',
  'пл.': 'площадь',
  'п.': 'поселок',
  'пр-д': 'проезд',
  'пр-к': 'просек',
  'пр-ка': 'просека',
  'пр-лок': 'проселок',
  'проул.': 'проулок',
  'рзд.': 'разъезд',
  'с.': 'село',
  'с-р': 'сквер',
  'сл.': 'слобода',
  'с-к': 'спуск',
  'ст.': 'станция',
  'стр.': 'строение',
  'тер. ПК': 'территория пк',
  'тер.СОСН': 'территория сосн',
  'тер. ТСЖ': 'территория тсж',
  'туп.': 'тупик',
  'ул.': 'улица',
  'х.': 'хутор',
  'ш.': 'шоссе',
  'з/у': 'земельный участок',
  'м/м': 'машино-место',

  // missing types from http://base.garant.ru/71249284/#block_1000

  'г.о. вн.д.': 'городской округ с внутригородским делением',
  'СП.': 'сельский поселок',
  'рп.': 'рабочий поселок',
  'кп.': 'курортный поселок',
  'гп.': 'городской поселок',
  'в-ки': 'выселки',
  'з-ка': 'заимка',
  'тер. СОСН': 'Территория СОСН',
  'г-ж': 'гараж',
  'кв.': 'квартира',
  'ком.': 'комната',
  'подв.': 'подвал',
  'п-б': 'погреб',
  офис: 'офис',
  'помещ.': 'помещение',
  'раб.уч.': 'рабочий участок',
  'скл.': 'склад',
  'торг.зал': 'торговый зал',
  цех: 'цех',
};
const houseTypes: Record<GarHouseType, string> = {
  None: '',
  Hold: 'владение',
  House: 'дом',
  HouseHold: 'домовладение',
  Garage: 'гараж',
  Building: 'здание',
  Mine: 'шахта',
  Structure: 'строение',
  Construction: 'сооружение',
  Litera: 'литера',
  Housing: 'корпус',
  Basement: 'подвал',
  BoilerRoom: 'котельная',
  Cellar: 'погреб',
  UnfinishedConstruction: 'объект нез-го стр-ва',
};

const houseAddTypes: Record<GarHouseAddType, string> = {
  None: '',
  Structure: 'строение',
  Construction: 'сооружение',
  Litera: 'литера',
  Housing: 'корпус',
};

const flatTypes: Record<FiasFlatType, string> = {
  0: '',
  1: 'помещение',
  2: 'квартира',
  3: 'офис',
  4: 'комната',
  5: 'рабочий участок',
  6: 'склад',
  7: 'торговый зал',
  8: 'цех',
  9: 'павильон',
  10: 'подвал',
  11: 'котельная',
  12: 'погреб',
  13: 'гараж',
  14: 'машино-место',
};

const roomTypes: Record<FiasRoomType, string> = {
  [FiasRoomType.None]: '',
  [FiasRoomType.Room]: 'комната',
  [FiasRoomType.Placement]: 'помещение',
};

const estateStatuses: Record<FiasEstateStatuses, string> = {
  None: '',
  Hold: 'владение',
  House: 'дом',
  HouseHold: 'домовладение',
  Garage: 'гараж',
  Building: 'здание',
  Mine: 'шахта',
  Basement: 'подвал',
  BoilerRoom: 'котельная',
  Cellar: 'погреб',
};

// TODO: Hide invisible fields without removing them.
export const filterVisibleFields = (
  fields: { [key in FiasFields]?: any },
  fieldsSettings: FiasFieldsSettings
): { [key in FiasFields]?: any } => {
  const filteredFields: { [key in FiasFields]?: any } = {};
  let field: FiasFields;
  for (field in fields) {
    if (isFieldVisible(field, fieldsSettings)) {
      filteredFields[field] = fields[field];
    }
  }
  return filteredFields;
};

export const isFieldVisible = (field: FiasAllFields, fieldsSettings: FiasFieldsSettings): boolean => {
  const settings = fieldsSettings[field];
  return Boolean(settings && settings.visible);
};

type EventSubscribe = { type: "ValidationChanged", callback: (newValue: ValidationChangedPayload) => void }
  | { type: "Blur", callback: () => void }
  | { type: "ValueChanged", callback: (v: FiasFullValue, options: ValueChangeOptions) => void }
  | { type: "InputValueChanged", callback: (query: string) => void }
  | { type: "UnexpectedInput", callback: (query: string) => void }
  | { type: "Focus", callback: (...params: unknown[]) => void };
export type ValidationChangedPayload = { error?: boolean | undefined; warning?: boolean | undefined };
export type ValueChangeOptions = { type: "fromModal" | "fromCombobox" };
type Event = { type: "ValidationChanged", payload: ValidationChangedPayload }
  | { type: "ValueChanged", payload: [FiasFullValue, ValueChangeOptions] }
  | { type: "Blur", payload: undefined }
  | { type: "InputValueChanged", payload: string }
  | { type: "UnexpectedInput", payload: string }
  | { type: "Focus", payload: unknown[] };
type EventToRecord<T extends Event = Event> = Record<T["type"], T["payload"]>;
const storeLastPayloadForEventTypes: Event["type"][] = ["ValidationChanged"];
export class EventBusConstructor {
  private readonly stack: Partial<Record<EventSubscribe["type"], Record<number, EventSubscribe["callback"]>>> = {};
  private lastId = 0;
  private generateId = () => this.lastId++;
  private readonly lastPayloads: Partial<EventToRecord> = {};

  subscribe({ type, callback }: EventSubscribe): { unsubscribe: () => void } {
    const id = this.generateId();
    if (!this.stack[type]) {
      this.stack[type] = {};
    }
    this.stack[type][id] = callback;
    if (this.lastPayloads[type] && storeLastPayloadForEventTypes.includes(type)) {
      const parameters = Array.isArray(this.lastPayloads[type]) ? this.lastPayloads[type] : [this.lastPayloads[type]];
      // @ts-ignore
      callback(...parameters);
    }
    return {
      unsubscribe: () => {
        delete this.stack[type]?.[id];
        if (Object.keys(this.stack[type] || {}).length === 0) {
          delete this.stack[type];
        }
      },
    };
  }

  publish(event: Event): void {
    if (storeLastPayloadForEventTypes.includes(event.type)) {
      this.lastPayloads[event.type] = cloneDeep(event.payload);
    }
    if (!this.stack[event.type]) return;
    Object.values(this.stack[event.type] || {}).forEach(value => {
      const parameters = Array.isArray(event.payload) ? event.payload : [event.payload];
      (value as Function)(...parameters);
    });
  }
}

const cloneDeep = <T extends any>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map(item => cloneDeep(item)) as T;
  } else if (typeof value === "object" && value !== null) {
    const newObj: T = {} as T;
    Object.keys(value).forEach(key => {
      // @ts-ignore
      newObj[key] = cloneDeep(value[key]);
    });
    return newObj as T;
  }

  return value;
}

export const FIAS_API_PREFIX = "/fias/v1.2/";
